import { useSearchParams } from "@umijs/max";
import { useEffect, useRef, useState } from "react";
import { useRequest } from "ahooks";
import { produce } from "immer";
import { nanoid } from "nanoid";
import { Button, message, Spin } from "antd";

import styles from "./index.less";

import {
  AskAIType,
  GeneratingStatus,
  streamFetch,
  StreamResponseDataType,
} from "@/utils/streamFetch";
import {
  CardMessage,
  ChatMessageType,
  clearChatHistoryService,
  FileMessage,
  getFlowDetailService,
  ImageMessage,
  InterruptConversationEnum,
  queryWelcomeWordsService,
  TextMessage,
  VideoInfoMessage,
  VoiceMessage,
} from "@/services/chat";
import { ResponseCode } from "@/constants";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";
import ChatItemAI from "./components/ChatItemAI";
import ChatItemHuman from "./components/ChatItemHuman";
import ChatInput from "./components/ChatInput";
import { getWebFingerprint } from "@/utils/fingerprint";

import { ReactComponent as ClearIcon } from "@/assets/images/chat/clear.svg";

type Role = "human" | "ai";

const aiChatHistory = "ai-chat-history";
const HISTORY_LIST = "HISTORY_LIST";

type ChatHistory = {
  msg: ChatMessageType;
  status: GeneratingStatus;
  role: Role;
  streamResponseData?: StreamResponseDataType[];
  id: string;
};

const Chat = () => {
  const [searchQuery] = useSearchParams();
  const appId = searchQuery.get("appId") as string;
  const [value, setValue] = useState("");

  const [chatHistory, setChatHistory] = useState<ChatHistory[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const aiChatHistoryIndexedDB = useRef<any>();

  const [needToInterrupt, setNeedToInterrupt] = useState<boolean>(true);
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [abortSignals, setAbortSignals] = useState<[string, AbortController][]>(
    []
  );

  const { data: appDetail, loading: appLoading } = useRequest(async () => {
    const rsp = await getFlowDetailService(appId);
    if (rsp.data?.name) {
      document.title = rsp.data.name;
    }
    setNeedToInterrupt(
      rsp.data.interruptOldOnNew === InterruptConversationEnum.NoInterrupt
        ? false
        : true
    );
    console.log(rsp.data);
    return rsp.data;
  });

  function updateData(data: { id: string; list: ChatHistory[] }) {
    const transaction = aiChatHistoryIndexedDB?.current?.transaction(
      [HISTORY_LIST],
      "readwrite"
    );
    const objectStore = transaction.objectStore(HISTORY_LIST);
    objectStore.put(data);
  }

  useEffect(() => {
    if (chatHistory?.length > 0) {
      updateData({ id: appId, list: chatHistory });
    }
  }, [chatHistory]);

  const { run: queryWelcomeWords } = useRequest(
    async () => {
      const rsp = await queryWelcomeWordsService(
        appId,
        `${appId}-${await getWebFingerprint()}`
      );
      if (rsp.code === ResponseCode.Success) {
        rsp.data.forEach((item) => {
          handleMessage(item);
        });
      }
    },
    {
      ready: !!appId,
    }
  );

  const removeLoadingStatusById = (updateId: string) => {
    setLoadingIds((state) => state.filter((id) => id !== updateId));
    setAbortSignals((state) => state.filter(([id]) => id !== updateId));
  };

  function getDataById() {
    const transaction = aiChatHistoryIndexedDB?.current.transaction([
      HISTORY_LIST,
    ]);
    const objectStore = transaction.objectStore(HISTORY_LIST);
    const request = objectStore.get(appId);

    request.onsuccess = function (event: { target: { result: any } }) {
      const res = event.target.result;
      if (res) {
        if (res?.list?.length > 0) {
          const aiChatHistoryList = JSON.parse(JSON.stringify(res?.list));
          const relAiChatHistoryList = aiChatHistoryList.filter(
            (item: { status: string }) => item.status === "done"
          );
          setChatHistory(relAiChatHistoryList);
          setTimeout(() => {
            containerRef.current?.scroll({
              top: containerRef.current?.scrollHeight,
              behavior: "smooth",
            });
          }, 500);
        }
      } else {
        console.log("No data found for ID:", appId);
      }
    };
  }

  useEffect(() => {
    const dbRequest = indexedDB.open(aiChatHistory, 1);
    dbRequest.onsuccess = function (event: any) {
      aiChatHistoryIndexedDB.current = event.target.result;
      getDataById();
    };
    dbRequest.onupgradeneeded = function (event: any) {
      const db = event?.target?.result;
      if (!db.objectStoreNames.contains(HISTORY_LIST)) {
        db.createObjectStore(HISTORY_LIST, {
          keyPath: "id",
          autoIncrement: true,
        });
      }
    };
  }, [appId]);

  function handleMessage(data: StreamResponseDataType) {
    // 文本输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.messageOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as TextMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { ...outputMap, type: "text" },
            id: nanoid(),
          });
        })
      );
    }
    // 文件输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.fileOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as FileMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { type: "file", name: outputMap.name, url: outputMap.url },
            id: nanoid(),
          });
        })
      );
    }
    // 视频输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.videoOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as VideoInfoMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { type: "video", name: outputMap.name, url: outputMap.url },
            id: nanoid(),
          });
        })
      );
    }
    // 图片输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.imageOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as ImageMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: { ...outputMap, type: "image" },
            id: nanoid(),
          });
        })
      );
    }
    // 卡片输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.cardOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as CardMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: {
              ...outputMap,
              type: "card",
            },
            id: nanoid(),
          });
        })
      );
    }
    // 语音输出
    if (
      data.dialog &&
      data.moduleInfo.flowType === FlowNodeTypeEnum.voiceOutput
    ) {
      const outputMap = data.moduleInfo.outputMap as VoiceMessage;
      setChatHistory(
        produce((state) => {
          state.push({
            role: "ai",
            status: "done",
            msg: {
              ...outputMap,
              type: "voice",
            },
            id: nanoid(),
          });
        })
      );
    }
  }

  async function handleAskAI(
    question: string,
    type: AskAIType,
    extraInfo?: any
  ) {
    if (needToInterrupt && loadingIds.length > 0) {
      handleStop();
    }
    const humanMsgId = nanoid();

    setLoadingIds(
      produce((state) => {
        state.push(humanMsgId);
      })
    );

    if (type === "TEXT") {
      setChatHistory(
        produce((state) => {
          state.push({
            role: "human",
            status: "running",
            msg: {
              // @ts-ignore 类型大小写的区别
              type: type.toLowerCase(),
              messageOutput: question,
            },
            id: humanMsgId,
          });
        })
      );
    }

    if (type === "FILE") {
      setChatHistory(
        produce((state) => {
          state.push({
            role: "human",
            status: "running",
            msg: {
              // @ts-ignore 类型大小写的区别
              type: "file",
              url: question,
              name: extraInfo.name,
            },
            id: humanMsgId,
          });
        })
      );
    }

    // 这几种类型定义不一致
    if (["IMAGE", "VOICE", "VIDEO"].includes(type)) {
      setChatHistory(
        produce((state) => {
          state.push({
            role: "human",
            status: "running",
            msg: {
              // @ts-ignore 类型大小写的区别
              type: type.toLowerCase(),
              url: question,
            },
            id: humanMsgId,
          });
        })
      );
    }

    setValue("");
    const abortSignal = new AbortController();
    setAbortSignals(
      produce((state) => {
        state.push([humanMsgId, abortSignal]);
      })
    );

    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 300);

    try {
      const { responseData } = await streamFetch({
        url: `/api/insnail-flow-platform/flow/runtime/test/${appId}`,
        credentials: "omit",
        data: {
          userChatInput: question,
          messageType: type,
          chatId: `${appId}-${await getWebFingerprint()}`,
        },
        onMessage(data) {
          setTimeout(() => {
            containerRef.current?.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: "smooth",
            });
          }, 100);
          if (data.status === "done") {
            removeLoadingStatusById(humanMsgId);
            setChatHistory(
              produce((state) => {
                const item = state.find((item) => item.id === humanMsgId);
                if (item) {
                  item.status = "done";
                }
              })
            );
          }
          if (!!data?.dialog) {
            handleMessage(data);
          }
        },
        onClose() {
          removeLoadingStatusById(humanMsgId);
          setChatHistory(
            produce((state) => {
              const item = state.find((item) => item.id === humanMsgId);
              if (item) {
                item.status = "done";
              }
            })
          );
        },
        abortCtrl: abortSignal,
      });

      removeLoadingStatusById(humanMsgId);
      setChatHistory(
        produce((state) => {
          const item = state.find((item) => item.id === humanMsgId);
          if (item) {
            item.status = "done";
            item.streamResponseData = responseData;
          }
        })
      );
    } catch (err) {
      message.error((err as Error).message);
      removeLoadingStatusById(humanMsgId);
      setChatHistory(
        produce((state) => {
          const item = state.find((item) => item.id === humanMsgId);
          if (item) {
            item.status = "done";
          }
        })
      );
    }
  }

  function handleStop() {
    if (loadingIds.length > 0) {
      setChatHistory(
        produce((state) => {
          for (let i = 0; i < state.length; i++) {
            const item = state[i];
            if (item && loadingIds.includes(item.id)) {
              item.status = "done";
            }
          }
        })
      );
      abortSignals.forEach((s) => s[1].abort());
      setLoadingIds([]);
      setAbortSignals([]);
    }
  }

  return (
    <div className={styles.container}>
      <Spin spinning={appLoading} className="w-full">
        <div className={styles.page}>
          <div className={styles.header}>
            <div className="flex flex-row justify-start items-center gap-[12px]">
              <div className="text-text-strong leading-[22px] text-[14px] font-medium">
                {appDetail?.name}
              </div>
            </div>

            <div className="flex flex-row justify-end items-center">
              <Button
                type="text"
                className="!w-[90px]"
                icon={<ClearIcon />}
                onClick={async () => {
                  if (needToInterrupt && loadingIds.length > 0) {
                    handleStop();
                  }
                  const rsp = await clearChatHistoryService(
                    `${appId}-${await getWebFingerprint()}`
                  );
                  if (rsp.code === ResponseCode.Success) {
                    message.success("清除成功");
                    updateData({ id: appId, list: [] });
                    setChatHistory([]);

                    queryWelcomeWords();
                  } else {
                    message.error(rsp.msg);
                  }
                }}
              >
                <div className="text-primary-600 text-[14px] leading-[22px]">
                  清空会话
                </div>
              </Button>
            </div>
          </div>

          <div className={styles.content} id="aiChatContent">
            <div className={styles["content-box"]} ref={containerRef}>
              {chatHistory.map((item) =>
                item.role === "human" ? (
                  <ChatItemHuman
                    msg={item.msg}
                    streamResponseData={item.streamResponseData}
                    key={item.id}
                    loading={item.status === "running"}
                    onRefresh={() => {
                      if (item.msg.type === "text") {
                        handleAskAI(item.msg.messageOutput, "TEXT");
                      }

                      if (item.msg.type === "file") {
                        handleAskAI(
                          item.msg.url,
                          item.msg.type.toUpperCase() as AskAIType,
                          {
                            name: item.msg.name,
                          }
                        );
                      }

                      if (["video", "image"].includes(item.msg.type)) {
                        handleAskAI(
                          // @ts-ignore 看类型定义
                          item.msg.url,
                          item.msg.type.toUpperCase() as AskAIType
                        );
                      }
                    }}
                  />
                ) : (
                  <ChatItemAI
                    avatar={appDetail?.avatar}
                    msg={item.msg}
                    key={item.id}
                    onDelete={() => {
                      //TODO: 删除
                    }}
                  />
                )
              )}
            </div>
          </div>

          <div className={styles.footer}>
            <div className={styles.input}>
              <ChatInput
                value={value}
                onChange={(v) => setValue(v)}
                onEnter={handleAskAI}
              />
            </div>
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default Chat;
